import { Box, Typography, useTheme } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { StyledParentBox } from "../MenuBar/partial/StyledBox.partial";

type FleetLocationFilterCardsProps = {
  locations: Array<{ id: number; name: string; picturePath?: string | null }>;
  selectedLocationIds: number[];
  onToggleLocation: (locationId: number) => void;
  onClear: () => void;
  displayMode?: "wrap" | "scroll";
};

type LocationFilterCardProps = {
  testId: string;
  isSelected: boolean;
  onClick: () => void;
  picturePath?: string;
  children: React.ReactNode;
  isScrollMode: boolean;
  cardSx: SxProps<Theme>;
};

function LocationFilterCard({
  testId,
  isSelected,
  onClick,
  picturePath,
  children,
  isScrollMode,
  cardSx
}: Readonly<LocationFilterCardProps>) {
  const theme = useTheme();
  const selectionSx = {
    boxShadow: isSelected
      ? `0 0 0 2px ${theme.palette.primary.main}`
      : `0 0 0 1px ${theme.palette.divider}`
  };

  if (isScrollMode) {
    return (
      <Box
        data-testid={testId}
        onClick={onClick}
        sx={{
          ...cardSx,
          ...selectionSx,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
          bgcolor: theme.palette.background.paper,
          borderRadius: 1
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <StyledParentBox
      data-testid={testId}
      picturePath={picturePath}
      onClick={onClick}
      sx={{ ...cardSx, ...selectionSx }}
    >
      {children}
    </StyledParentBox>
  );
}

export function FleetLocationFilterCards({
  locations,
  selectedLocationIds,
  onToggleLocation,
  onClear,
  displayMode = "wrap"
}: Readonly<FleetLocationFilterCardsProps>) {
  const { t } = useTranslation();
  const isScrollMode = displayMode === "scroll";

  const cardSx: SxProps<Theme> = isScrollMode
    ? {
        minHeight: { xs: 40, sm: 44 },
        px: { xs: 1.25, sm: 1.5 },
        py: { xs: 0.75, sm: 1 },
        fontSize: { xs: 13, sm: 14 },
        maxWidth: { xs: 148, md: "none" },
        flexShrink: { xs: 0, md: "initial" }
      }
    : {
        minWidth: "140px",
        minHeight: "58px",
        padding: "10px 12px",
        fontSize: 14,
        borderRadius: "8px"
      };

  const wrapContainerSx: SxProps<Theme> = {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 1
  };
  const scrollContainerSx: SxProps<Theme> = {
    display: "flex",
    alignItems: "center",
    flexWrap: { xs: "nowrap", md: "wrap" },
    gap: { xs: 0.75, sm: 1 },
    maxWidth: "100%",
    overflowX: { xs: "auto", md: "visible" },
    overscrollBehaviorX: { xs: "contain", md: "auto" },
    WebkitOverflowScrolling: "touch",
    py: 0.5
  };
  const containerSx = isScrollMode ? scrollContainerSx : wrapContainerSx;

  return (
    <Box sx={containerSx}>
      <LocationFilterCard
        testId="fleet-location-filter-card-all"
        isSelected={selectedLocationIds.length === 0}
        onClick={onClear}
        isScrollMode={isScrollMode}
        cardSx={cardSx}
      >
        <Typography noWrap sx={{ fontSize: "inherit", fontWeight: 600, lineHeight: 1.2 }}>
          {t("All locations")}
        </Typography>
      </LocationFilterCard>

      {locations.map(location => {
        const isSelected = selectedLocationIds.includes(location.id);
        return (
          <LocationFilterCard
            key={location.id}
            testId={`fleet-location-filter-card-${location.id}`}
            picturePath={location.picturePath ?? undefined}
            onClick={() => onToggleLocation(location.id)}
            isSelected={isSelected}
            isScrollMode={isScrollMode}
            cardSx={cardSx}
          >
            <Typography
              noWrap
              sx={{
                fontSize: "inherit",
                fontWeight: 600,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {location.name}
            </Typography>
          </LocationFilterCard>
        );
      })}
    </Box>
  );
}
